import type { RoomSearchProgress } from '@elara/domain/flows';
import type { Occupancy } from '@elara/domain/models/schemas/room-schemas';

import type { RoomOptionsContainer } from '#containers/RoomOptionsContainer.js';
import type { QueryHandle } from '#firebase/types.js';
import type { AppFlowsContainer } from '#flows.js';
import type { RoomOptionModel } from '#models/RoomOptionModel.js';

import { differenceInDays } from 'date-fns';

import { errorMessage } from '@elara/core/errors';
import { orderBy, where } from '@elara/firebolt/shared';

import { trackRoomSearch, trackRoomSearchResultsViewed } from '#lib/analytics-events.js';
import { EMPTY_QUERY } from '#lib/firebolt-utils.js';
import { createState } from '#lib/signals-utils.js';

/** What the traveller is searching for. Dates are YYYY-MM-DD. */
export interface RoomSearchForm {
  startDate?: string;
  endDate?: string;
  occupancy: Occupancy;
}

/**
 * Where the room search is. Explicit rather than a set of flags, so "searching" and "showing an
 * error" can never both be true.
 *
 * - `idle`: nothing open.
 * - `loading`: fetching the pre-fill.
 * - `ready`: the form is usable; results (if any) are those of `activeSearchId`.
 * - `searching`: a search or refresh is running; `progress` is the stage the server last reported.
 * - `empty`: the last search found nothing; `message` says why, in the traveller's words.
 * - `error`: the last request failed.
 */
export type RoomSearchPhase =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'searching'; mode: 'search' | 'refresh'; progress?: RoomSearchProgress }
  | { kind: 'empty'; message: string }
  | { kind: 'error'; message: string };

/** Results older than this get a "prices may have changed" nudge next to Refresh. */
export const ROOM_SEARCH_STALE_AFTER_MS = 24 * 60 * 60 * 1000;

/**
 * Results younger than this need no "checked … ago" line or Refresh: the traveller has just searched.
 * Not reactive on its own (`Date.now()`), so a view showing the line schedules its own re-render.
 */
export const ROOM_SEARCH_AGED_AFTER_MS = 5 * 60 * 1000;

const DEFAULT_FORM: RoomSearchForm = { occupancy: { adults: 2 } };

/** Today as the server judges it (UTC), YYYY-MM-DD. */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function leadTotal(room: RoomOptionModel): number {
  return room.leadRate?.localPricing.totalPrice ?? Number.POSITIVE_INFINITY;
}

/**
 * Shared store for the hotel guide's Rooms tab (ELARA-1092), used by web and mobile alike.
 *
 * The traveller searches a hotel's rooms themselves instead of asking the concierge. The server runs
 * the concierge's own search (`SearchHotelRooms`), curated picks included, and saves every room
 * under the trip with one `searchId`; this store lists those rooms reactively by `searchId`, so the
 * same view serves a fresh search, the last search of the hotel, and "View all results" from a chat
 * card (which opens an existing search and never runs a new one).
 *
 * The form opens pre-filled (`GetRoomSearchDefaults`): the hotel's last search, then the trip's,
 * then the concierge's working memory, then the trip dates.
 *
 * Reads the current trip and chat through narrow slices of their stores, so tests can hand in
 * plain objects.
 */
export function createHotelRoomSearchState(
  tripsState: { readonly current?: { readonly id?: string } | undefined },
  chatsState: { readonly current?: { readonly id?: string } | undefined },
  flows: AppFlowsContainer,
  roomOptions: RoomOptionsContainer,
) {
  /** Bumped by every open/reset; an async result for an older token is dropped. */
  let generation = 0;

  return createState({
    tripId: undefined as string | undefined,
    hotelId: undefined as string | undefined,
    form: DEFAULT_FORM as RoomSearchForm,
    /** True once the traveller edits the form, so a late pre-fill never overwrites their input. */
    formTouched: false,
    /**
     * Bumped each time the pre-fill writes the form, so a view holding its own copy of the form (the
     * per-child age slots) can re-seed — the source alone repeats when two searches are opened in turn.
     */
    prefillVersion: 0,
    phase: { kind: 'idle' } as RoomSearchPhase,
    /** The search whose rooms are listed. */
    activeSearchId: undefined as string | undefined,
    /**
     * An open request handed over by whoever opens the guide ("View all results" under a chat card):
     * the guide consumes it on mount. Lives here rather than in the URL because the native navigation
     * port carries only the guide's type and id.
     */
    pendingOpen: undefined as { hotelId: string; searchId?: string } | undefined,

    /** Every room of the active search, as Firestore holds them now. */
    get resultsQuery(): QueryHandle<RoomOptionModel> {
      if (!this.tripId || !this.activeSearchId) return EMPTY_QUERY as unknown as QueryHandle<RoomOptionModel>;
      return roomOptions.ClientRoomOption.list(
        { tripId: this.tripId },
        where('searchId', '==', this.activeSearchId),
        orderBy('fetchedAt', 'asc'),
      ) as unknown as QueryHandle<RoomOptionModel>;
    },

    get isResultsReady(): boolean {
      return this.resultsQuery.isReady;
    },

    /** The active search's rooms, minus any a later identity change replaced. */
    get rooms(): RoomOptionModel[] {
      return this.resultsQuery.items.filter((room) => !room.supersededBy);
    },

    /** Elara's picks, in the order the search ranked them. */
    get picks(): RoomOptionModel[] {
      return this.rooms
        .filter((room) => room.recommendedRate)
        .toSorted(
          (a, b) => (a.recommendedRate?.recommendation?.rank ?? 99) - (b.recommendedRate?.recommendation?.rank ?? 99),
        );
    },

    /** Every other room, cheapest first; rooms with nothing left to book go last. */
    get otherRooms(): RoomOptionModel[] {
      return this.rooms.filter((room) => !room.recommendedRate).toSorted((a, b) => leadTotal(a) - leadTotal(b));
    },

    /** When the listed rates were last checked with the hotel. */
    get ratesCheckedAt(): Date | undefined {
      let latest: Date | undefined;
      for (const room of this.rooms) {
        const at = room.fetchedAt;
        if (at instanceof Date && (!latest || at > latest)) latest = at;
      }
      return latest;
    },

    // The two age checks are methods, not getters: the store caches a getter as a computed signal, and
    // the clock is no signal, so a getter read once while the rates were fresh stayed fresh for good and
    // the view's timer re-rendered onto the same answer (review, 2026-10-20).

    /** The listed rates are old enough that prices may have changed. */
    areRatesStale(now: number = Date.now()): boolean {
      const at = this.ratesCheckedAt;
      return !!at && now - at.getTime() > ROOM_SEARCH_STALE_AFTER_MS;
    },

    /** The listed rates are old enough to say when they were checked; unknown age counts as old. */
    areRatesAged(now: number = Date.now()): boolean {
      const at = this.ratesCheckedAt;
      return !at || now - at.getTime() > ROOM_SEARCH_AGED_AFTER_MS;
    },

    get isSearching(): boolean {
      return this.phase.kind === 'searching';
    },

    /** Why the form cannot be searched as it stands, in the traveller's words; undefined when it can. */
    get formError(): string | undefined {
      const { startDate, endDate, occupancy } = this.form;
      if (!startDate || !endDate) return;
      if (startDate < todayIso()) return 'Check-in cannot be in the past.';
      if (endDate <= startDate) return 'Check-out must be after check-in.';
      if (occupancy.adults < 1) return 'Add at least one adult.';
      const children = occupancy.children ?? 0;
      if (children > 0 && (occupancy.childrenAges?.length ?? 0) !== children) {
        return "Add each child's age, so the hotel can price the room.";
      }
      return;
    },

    /**
     * Missing dates are not a `formError` — the empty date field says what is needed — but they still
     * hold the search back, so the button cannot be pressed to no effect.
     */
    get canSearch(): boolean {
      const { startDate, endDate } = this.form;
      return !!this.tripId && !!this.hotelId && !!startDate && !!endDate && !this.formError && !this.isSearching;
    },

    get canRefresh(): boolean {
      return !!this.tripId && !!this.hotelId && !!this.activeSearchId && !this.isSearching;
    },

    get nights(): number {
      const { startDate, endDate } = this.form;
      return startDate && endDate ? Math.max(0, differenceInDays(new Date(endDate), new Date(startDate))) : 0;
    },

    /**
     * Asks the hotel guide to open on a search, then opens it — the "View all results" path. The
     * guide's own `open` picks the request up; the event records that a chat card led here.
     */
    requestOpen(request: { hotelId: string; searchId?: string }): void {
      this.pendingOpen = request;
      const tripId = tripsState.current?.id;
      if (request.searchId && tripId) {
        trackRoomSearchResultsViewed({ tripId, hotelId: request.hotelId, entry: 'chat_view_all' });
      }
    },

    /**
     * Opens the Rooms tab for a hotel in the current trip. A `searchId` (or a pending request for
     * this hotel) shows that search's rooms at once; otherwise the hotel's last search, if any, is
     * shown once the pre-fill arrives. Re-opening the hotel already open keeps its state unless a
     * different search is asked for.
     */
    open(hotelId: string, options: { searchId?: string } = {}): void {
      const tripId = tripsState.current?.id;
      const pending = this.pendingOpen?.hotelId === hotelId ? this.pendingOpen : undefined;
      if (this.pendingOpen && this.pendingOpen.hotelId !== hotelId) this.pendingOpen = undefined;
      const searchId = options.searchId ?? pending?.searchId;
      if (pending) this.pendingOpen = undefined;

      const sameHotel = this.hotelId === hotelId && this.tripId === tripId && this.phase.kind !== 'idle';
      if (sameHotel) {
        if (searchId && searchId !== this.activeSearchId) {
          // A new request: a pre-fill or search still in flight for the old one must not land on this one,
          // or the stay above the rooms would be another search's.
          generation += 1;
          this.activeSearchId = searchId;
          // A search or its outcome was for the old one; the named search's rooms are what to show.
          if (this.phase.kind !== 'loading') this.phase = { kind: 'ready' };
          // Asked for by name ("View all results"), so the form follows it even over earlier edits:
          // the stay and party shown must be the ones those rooms were priced for.
          this.formTouched = false;
          void this.loadDefaults(searchId);
        }
        return;
      }

      this.reset();
      this.tripId = tripId;
      this.hotelId = hotelId;
      this.activeSearchId = searchId;
      if (!tripId) return;
      this.phase = { kind: 'loading' };
      void this.loadDefaults(searchId);
    },

    /**
     * Fetches the pre-fill — the stay and party of `searchId` when the guide was opened on one, so the
     * form matches the rooms listed. Never fails the tab: on error the form keeps its own defaults.
     */
    async loadDefaults(searchId?: string): Promise<void> {
      const { tripId, hotelId } = this;
      if (!tripId || !hotelId) return;
      const token = generation;
      try {
        const { data } = await flows.getRoomSearchDefaults({
          tripId,
          hotelId,
          chatId: chatsState.current?.id,
          ...(searchId ? { searchId } : {}),
        });
        if (token !== generation) return;
        if (!this.formTouched) {
          this.form = {
            ...(data.startDate ? { startDate: data.startDate } : {}),
            ...(data.endDate ? { endDate: data.endDate } : {}),
            occupancy: data.occupancy,
          };
          this.prefillVersion += 1;
        }
        if (!this.activeSearchId && data.lastSearch) this.activeSearchId = data.lastSearch.searchId;
      } catch (error) {
        if (token !== generation) return;
        // The form still works on its own defaults; the pre-fill is a convenience.
        console.warn('[HotelRoomSearchState] Could not load room search defaults', errorMessage(error));
      }
      if (token === generation && this.phase.kind === 'loading') this.phase = { kind: 'ready' };
    },

    setDates(startDate: string | undefined, endDate: string | undefined): void {
      this.formTouched = true;
      const { startDate: _s, endDate: _e, ...rest } = this.form;
      this.form = { ...rest, ...(startDate ? { startDate } : {}), ...(endDate ? { endDate } : {}) };
    },

    setOccupancy(occupancy: Occupancy): void {
      this.formTouched = true;
      this.form = { ...this.form, occupancy };
    },

    /** Runs a new search for the form as it stands. */
    async search(): Promise<void> {
      const { tripId, hotelId } = this;
      const { startDate, endDate, occupancy } = this.form;
      if (!this.canSearch || !tripId || !hotelId || !startDate || !endDate) return;
      await this.run('search', {
        kind: 'search',
        tripId,
        hotelId,
        startDate,
        endDate,
        occupancy,
        chatId: chatsState.current?.id,
      });
    },

    /** Re-prices the listed search in place: same rooms and picks, current rates. */
    async refresh(): Promise<void> {
      const { tripId, hotelId, activeSearchId } = this;
      if (!this.canRefresh || !tripId || !hotelId || !activeSearchId) return;
      await this.run('refresh', { kind: 'refresh', tripId, hotelId, searchId: activeSearchId });
    },

    async run(
      mode: 'search' | 'refresh',
      input: Parameters<AppFlowsContainer['searchHotelRooms']['stream']>[0],
    ): Promise<void> {
      const token = generation;
      const { tripId, hotelId } = this;
      const form = this.form;
      this.phase = { kind: 'searching', mode };
      try {
        const { stream, data } = await flows.searchHotelRooms.stream(input);
        for await (const chunk of stream) {
          if (token !== generation) continue;
          if ('stage' in chunk) this.phase = { kind: 'searching', mode, progress: chunk };
        }
        const output = await data;
        if (token !== generation) return;

        if (output.status === 'empty') {
          // A new search that found nothing must not leave the previous stay's rooms on screen; a
          // refresh that could not reach the hotel leaves the rooms it was refreshing.
          if (mode === 'search') {
            this.activeSearchId = undefined;
          }
          this.phase = { kind: 'empty', message: output.message ?? 'No rooms are available for this search.' };
          return;
        }

        this.activeSearchId = output.searchId ?? this.activeSearchId;
        this.phase = { kind: 'ready' };
        if (tripId && hotelId) {
          trackRoomSearch({
            tripId,
            hotelId,
            nights: form.startDate && form.endDate ? this.nights : 0,
            adults: form.occupancy.adults,
            children: form.occupancy.children ?? 0,
            mode,
          });
        }
      } catch (error) {
        if (token !== generation) return;
        this.phase = {
          kind: 'error',
          message: errorMessage(error, { firstLineOnly: true, fallbackMessage: 'The room search failed. Try again.' }),
        };
      }
    },

    reset(): void {
      generation += 1;
      this.tripId = undefined;
      this.hotelId = undefined;
      this.form = DEFAULT_FORM;
      this.formTouched = false;
      this.phase = { kind: 'idle' };
      this.activeSearchId = undefined;
    },
  });
}

/** The hotel guide's room search: pre-fill, search, refresh, and the rooms of the active search. */
export type HotelRoomSearchState = ReturnType<typeof createHotelRoomSearchState>;
